import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/slices/authSlice';
import { selectStoreAndOutlet } from '@/store/slices/storeSlice';

interface SubscriptionGuardResult {
  isExpired: boolean;
  showExpiredModal: boolean;
  setShowExpiredModal: (show: boolean) => void;
}

// setTimeout max delay: 2^31 - 1 ms (~24.8 days)
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Hook to monitor subscription status for the current store.
 * - Checks subscription endDate from user data
 * - Sets a timeout to auto-show expired modal when subscription expires during active session
 * - Listens for SUBSCRIPTION_EXPIRED events dispatched from axios interceptor
 */
export const useSubscriptionGuard = (): SubscriptionGuardResult => {
  const user = useSelector(selectUser);
  const { storeId } = useSelector(selectStoreAndOutlet);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkSubscription = useCallback(() => {
    if (!user?.subscriptions || !storeId) {
      return false;
    }

    const subscription = user.subscriptions.find(
      (s) => s.storeId === storeId
    );

    if (!subscription) {
      return false;
    }

    const now = new Date();
    const endDate = new Date(subscription.endDate);

    return (
      endDate < now ||
      subscription.status === 'EXPIRED' ||
      subscription.status === 'CANCELLED'
    );
  }, [user, storeId]);

  const isExpired = checkSubscription();

  // Sync modal with expired state.
  // Depends on user & storeId so it re-runs on login/logout/store-switch,
  // even when isExpired stays false (App never unmounts → state persists).
  useEffect(() => {
    if (isExpired) {
      setShowExpiredModal(true);
    } else {
      setShowExpiredModal(false);
    }
  }, [isExpired, user, storeId]);

  // Set timeout for subscription expiring during active session.
  // Only set if remaining time is within setTimeout's 32-bit limit (~24.8 days).
  // For longer durations, the periodic re-render from user activity will catch it.
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!user?.subscriptions || !storeId || isExpired) {
      return;
    }

    const subscription = user.subscriptions.find(
      (s) => s.storeId === storeId
    );

    if (!subscription) return;

    const now = new Date().getTime();
    const endDate = new Date(subscription.endDate).getTime();
    const remainingMs = endDate - now;

    if (remainingMs > 0 && remainingMs <= MAX_TIMEOUT_MS) {
      timeoutRef.current = setTimeout(() => {
        setShowExpiredModal(true);
      }, remainingMs);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, storeId, isExpired]);

  // Listen for SUBSCRIPTION_EXPIRED event from axios interceptor
  useEffect(() => {
    const handler = () => {
      setShowExpiredModal(true);
    };

    window.addEventListener('subscription-expired', handler);
    return () => {
      window.removeEventListener('subscription-expired', handler);
    };
  }, []);

  return {
    isExpired,
    showExpiredModal,
    setShowExpiredModal,
  };
};
